<template>
  <div class="bottom-container">
    <div class="breadcrumb-section">
      <el-breadcrumb separator="/">
        <!-- 工作台永远固定显示，不可关闭，activeIndex 为 -1 时高亮 -->
        <el-breadcrumb-item>
          <span class="crumb-item" :class="{ 'is-active': activeIndex === -1 }">
            <span class="text" @click="handleHomeClick">工作台</span>
          </span>
        </el-breadcrumb-item>
        
        <!-- 显示的历史路由列表 -->
        <el-breadcrumb-item v-for="(item, index) in displayHistory" :key="item.realIndex">
          <span class="crumb-item" :class="{ 'is-active': item.realIndex === activeIndex }">
            <span class="text" @click="handleTagClick(item.realIndex)">{{ item.title }}</span>
            <i class="el-icon-close close-icon" @click.stop="closeTag(item.realIndex)"></i>
          </span>
        </el-breadcrumb-item>

        <!-- 更多下拉菜单（当总数超过 10 个时出现） -->
        <el-breadcrumb-item v-if="hiddenHistory.length > 0">
          <el-dropdown trigger="hover" @command="handleDropdownClick">
            <span class="more-btn" :class="{ 'is-more-active': isActiveHidden }">
              更多 <i class="el-icon-arrow-down el-icon--right"></i>
            </span>
            <el-dropdown-menu slot="dropdown" class="more-dropdown">
              <el-dropdown-item v-for="item in hiddenHistory" :key="item.realIndex" :command="item.realIndex">
                <div class="dropdown-item-content">
                  <span class="dropdown-text">{{ item.title }}</span>
                  <i class="el-icon-close close-icon dropdown-close" @click.stop="closeTag(item.realIndex)"></i>
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <div class="info-section">
      <span>{{ staffName }}</span>
      <span class="divider">|</span>
      <span>{{ deptName }}</span>
      <span class="divider">|</span>
      <span>当前角色：{{ currentRoleName }}</span>
    </div>
  </div>
</template>

<script>
import { EventBus } from '@/directive/event-bus.js';

export default {
  data() {
    return {
      historyList: [], // 所有历史节点
      activeIndex: -1, // 当前激活的节点索引 (-1 表示工作台)
      staffName: 'admin',
      deptName: '未分配科室',
      currentRoleName: '',
      maxVisibleCount: 10, // 最多显示多少个标签
    };
  },
  computed: {
    // 计算当前显示的节点
    displayHistory() {
      const len = this.historyList.length;
      if (len <= this.maxVisibleCount) {
        return this.historyList.map((item, index) => ({ ...item, realIndex: index }));
      }
      // 如果激活的节点在后面，为了能看到高亮，截取最后 10 个
      if (this.activeIndex >= this.maxVisibleCount) {
        return this.historyList.slice(-this.maxVisibleCount).map((item, index) => ({ 
          ...item, 
          realIndex: len - this.maxVisibleCount + index 
        }));
      }
      // 否则截取前 10 个
      return this.historyList.slice(0, this.maxVisibleCount).map((item, index) => ({ 
        ...item, 
        realIndex: index 
      }));
    },
    // 计算隐藏起来进入“更多”下拉的节点
    hiddenHistory() {
      const len = this.historyList.length;
      if (len <= this.maxVisibleCount) return [];
      
      if (this.activeIndex >= this.maxVisibleCount) {
        // 激活的在后面，前面的进更多
        return this.historyList.slice(0, len - this.maxVisibleCount).map((item, index) => ({ 
          ...item, 
          realIndex: index 
        }));
      }
      // 激活的在前面，后面的进更多
      return this.historyList.slice(this.maxVisibleCount).map((item, index) => ({ 
        ...item, 
        realIndex: index + this.maxVisibleCount 
      }));
    },
    // 如果当前选中的节点在隐藏列表中，让“更多”按钮高亮提示
    isActiveHidden() {
      return this.activeIndex >= this.maxVisibleCount;
    }
  },
  watch: {
    '$route': {
      handler(to) {
        if (to.path === '/home' || to.path === '/') {
          this.historyList = [];
          this.activeIndex = -1;
          this.saveHistory();
        }
      },
      immediate: true,
      deep: true
    }
  },
  created() {
    this.initUserInfo();
    this.initRole();
    
    const savedHistory = sessionStorage.getItem('breadcrumbHistory');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        this.historyList = parsed.list || [];
        this.activeIndex = parsed.activeIndex !== undefined ? parsed.activeIndex : -1;
      } catch (e) {
        this.historyList = [];
        this.activeIndex = -1;
      }
    }
    
    EventBus.$on('role-change', this.handleRoleChange);
    EventBus.$on('dept-change', this.handleDeptChange);
    EventBus.$on('add-breadcrumb', this.handleAddBreadcrumb);
  },
  beforeDestroy() {
    EventBus.$off('role-change', this.handleRoleChange);
    EventBus.$off('dept-change', this.handleDeptChange);
    EventBus.$off('add-breadcrumb', this.handleAddBreadcrumb);
  },
  methods: {
    // 处理新的点击
    handleAddBreadcrumb(data) {
      if (!data || !data.title) return;
      
      const existIndex = this.historyList.findIndex(h => h.title === data.title);
      
      if (existIndex > -1) {
        // 如果已存在，仅改变高亮位置，不追加
        this.activeIndex = existIndex;
      } else {
        // 如果不存在，追加到末尾
        this.historyList.push({
          title: data.title,
          path: data.path,
          payload: data.payload
        });
        this.activeIndex = this.historyList.length - 1;
      }
      
      // 防止数组无限增长，设置一个安全上限（比如50个）
      if (this.historyList.length > 50) {
        const diff = this.historyList.length - 50;
        this.historyList = this.historyList.slice(diff);
        this.activeIndex = Math.max(0, this.activeIndex - diff);
      }
      
      this.saveHistory();
    },

    // 点击工作台
    handleHomeClick() {
      this.activeIndex = -1;
      this.saveHistory();
      this.$router.push('/home').catch(err => {
        if (err.name !== 'NavigationDuplicated') console.error(err);
      });
    },

    // 点击某个已存在的标签
    handleTagClick(realIndex) {
      if (realIndex === this.activeIndex) return;
      
      this.activeIndex = realIndex;
      this.saveHistory();
      
      const targetItem = this.historyList[realIndex];
      this.navigateToRoute(targetItem);
    },

    // 下拉菜单的点击处理
    handleDropdownClick(realIndex) {
      this.handleTagClick(realIndex);
    },

    // 处理跳转逻辑
    navigateToRoute(item) {
      if (!item) return;
      if (item.path === '/Router' && item.payload) {
        sessionStorage.setItem('pendingMenuData', JSON.stringify(item.payload));
        if (this.$route.path === '/Router') {
          EventBus.$emit('menu-data-update', item.payload);
        } else {
          this.$router.push('/Router').catch(err => {
            if (err.name !== 'NavigationDuplicated') console.error(err);
          });
        }
      } else {
        this.$router.push(item.path).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
      }
    },

    // 关闭某个标签
    closeTag(realIndex) {
      if (realIndex === -1) return; // 工作台不可关闭
      
      const isCurrent = realIndex === this.activeIndex;
      
      // 移除该项
      this.historyList.splice(realIndex, 1);
      
      if (isCurrent) {
        if (this.historyList.length === 0) {
          this.handleHomeClick();
          return;
        } else {
          // 关闭当前页，自动跳转到前一个（如果前面没了就跳第一个）
          const newIndex = Math.max(0, realIndex - 1);
          this.activeIndex = newIndex;
          this.navigateToRoute(this.historyList[newIndex]);
        }
      } else if (realIndex < this.activeIndex) {
        // 关闭的是前面的，当前索引-1
        this.activeIndex--;
      }
      
      this.saveHistory();
    },

    saveHistory() {
      sessionStorage.setItem('breadcrumbHistory', JSON.stringify({
        list: this.historyList,
        activeIndex: this.activeIndex
      }));
    },
    
    initUserInfo() {
      const userinfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      this.staffName = userinfo.staffName || 'admin';
      const deptOptions = (userinfo.deptDepts || []).map(item => ({ id: item.id, label: item.deptName }));
      if (userinfo.selDept) {
        const dept = deptOptions.find(d => d.id === userinfo.selDept);
        this.deptName = dept ? dept.label : '未分配科室';
      } else if (deptOptions.length > 0) {
        this.deptName = deptOptions[0].label;
      }
    },
    initRole() {
      try {
        const rolesStr = localStorage.getItem('userRoles');
        if (rolesStr) {
          const roles = JSON.parse(rolesStr);
          if (Array.isArray(roles) && roles.length > 0) {
            const firstRole = roles[0];
            this.currentRoleName = firstRole.roleName || firstRole.name || firstRole.label || firstRole;
          }
        }
      } catch (e) {}
    },
    handleRoleChange(role) {
      this.currentRoleName = role.roleName || role.name || role.label || role;
    },
    handleDeptChange(deptName) {
      this.deptName = deptName;
    }
  }
};
</script>

<style lang="less" scoped>
.bottom-container {
  height: 40px;
  background-color: #f5f7fa;
  border-bottom: 1px solid #e6e6e6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  box-sizing: border-box;
  color: #606266;
  flex-shrink: 0;
  width: 100%;

  .breadcrumb-section {
    display: flex;
    align-items: center;
    flex: 1;
    overflow: hidden;
    
    /deep/ .el-breadcrumb {
      font-size: 13px;
      line-height: 1;
      white-space: nowrap;
      overflow: hidden;
      display: flex;
      align-items: center;
      width: 100%;
      
      .el-breadcrumb__item {
        float: none;
        display: flex;
        align-items: center;
        margin-bottom: 0;
      }
      
      .el-breadcrumb__separator {
        margin: 0 8px;
        color: #dcdfe6;
      }
    }

    /* 标签节点样式 */
    .crumb-item {
      display: inline-flex;
      align-items: center;
      cursor: pointer;
      padding: 4px 6px;
      border-radius: 4px;
      transition: all 0.2s;
      
      &:hover {
        background-color: #eef1f6;
      }

      &.is-active {
        .text {
          color: #409eff;
          font-weight: 600;
        }
      }

      .text {
        color: #606266;
        font-size: 13px;
        line-height: 1;
        transition: color 0.2s;
        
        &:hover {
          color: #409eff;
        }
      }

      /* 关闭按钮 */
      .close-icon {
        font-size: 12px;
        color: #909399;
        margin-left: 4px;
        border-radius: 50%;
        padding: 2px;
        transition: all 0.2s;
        
        &:hover {
          color: #fff;
          background: #f56c6c;
        }
      }
    }

    /* 更多按钮样式 */
    .more-btn {
      display: inline-flex;
      align-items: center;
      color: #606266;
      cursor: pointer;
      padding: 4px 6px;
      border-radius: 4px;
      transition: all 0.2s;
      background-color: #f0f2f5;
      
      &:hover {
        background-color: #e6f7ff;
        color: #409eff;
      }

      &.is-more-active {
        color: #409eff;
        font-weight: 600;
      }
    }
  }
  
  .info-section {
    display: flex;
    align-items: center;
    font-size: 12px;
    color: #909399;
    flex-shrink: 0;
    margin-left: 16px;
    
    .divider { margin: 0 8px; color: #dcdfe6; }
  }
}

/* 下拉菜单内部样式 */
.more-dropdown {
  .dropdown-item-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: 140px;
    
    .dropdown-text {
      flex: 1;
      margin-right: 16px;
      font-size: 13px;
    }
    
    .dropdown-close {
      font-size: 12px;
      color: #c0c4cc;
      transition: all 0.2s;
      
      &:hover {
        color: #f56c6c;
      }
    }
  }
}

@media (max-width: 768px) {
  .bottom-container {
    padding: 0 12px;
    .info-section {
      display: none;
    }
  }
}
</style>